import { CoreError, ValidationError } from '../errors/core';
import {
  InstrumentDefinition,
  InstrumentType,
  MusicTheoryService,
  Pitch,
  TuningDefinition,
} from '../music-theory-service';
import { INSTRUMENTS_BY_TYPE } from '../music-theory-service/instruments';
import { FretPosition } from './types';
import { computeFretPositions } from './positions';

export interface FretboardOptions {
  instrumentType: InstrumentType;
  tuningId?: string;
  tuning?: Pitch[];
  numFrets?: number;
}

export class FretboardModel {
  private readonly instrument: InstrumentDefinition;
  private readonly tuning: TuningDefinition;
  private readonly numStrings: number;
  private readonly numFrets: number;
  private positions: Map<string, FretPosition>;

  constructor(options: FretboardOptions) {
    const instrumentDefinition = INSTRUMENTS_BY_TYPE[options.instrumentType];
    if (!instrumentDefinition) {
      throw new ValidationError(
        `Unsupported instrument type: ${options.instrumentType}`,
        'UNSUPPORTED_INSTRUMENT_TYPE',
        {
          instrumentType: options.instrumentType,
        }
      );
    }
    this.instrument = instrumentDefinition;

    let selectedTuning: TuningDefinition | undefined;

    if (options.tuning) {
      const expectedLength = instrumentDefinition.defaultTunings[0]?.pitches.length;
      if (!expectedLength || options.tuning.length !== expectedLength) {
        throw new ValidationError(
          'Custom tuning array length must match the expected number of strings for the instrument.',
          'INVALID_TUNING_LENGTH',
          {
            expectedLength: expectedLength ?? 'unknown',
            actualLength: options.tuning.length,
            instrument: this.instrument.name,
          }
        );
      }
      selectedTuning = {
        // Create a "custom" TuningDefinition
        id: 'custom-tuning',
        label: 'Custom Tuning',
        pitches: options.tuning,
      };
    } else if (options.tuningId) {
      selectedTuning = this.instrument.defaultTunings.find((t) => t.id === options.tuningId);
      if (!selectedTuning) {
        throw new ValidationError(
          `Tuning with id '${options.tuningId}' not found for instrument '${this.instrument.name}'.`,
          'TUNING_NOT_FOUND',
          {
            tuningId: options.tuningId,
            instrumentName: this.instrument.name,
          }
        );
      }
    } else {
      selectedTuning = this.instrument.defaultTunings[0]; // Default to first tuning if none provided
      if (!selectedTuning) {
        throw new CoreError(
          `No default tuning defined for instrument '${this.instrument.name}'.`,
          'MISSING_DEFAULT_TUNING',
          { instrumentName: this.instrument.name }
        );
      }
    }
    this.tuning = selectedTuning;

    this.numStrings = this.tuning.pitches.length; // Dynamically determine from tuning
    this.numFrets = options.numFrets ?? this.instrument.defaultNumFrets; // Use provided or default frets

    this.positions = this.initializePositions();
  }

  private initializePositions(): Map<string, FretPosition> {
    return computeFretPositions({
      numStrings: this.numStrings,
      numFrets: this.numFrets,
      tuning: this.tuning,
      instrumentName: this.instrument.name,
    });
  }

  getPositionKey(string: number, fret: number): string {
    return `${string}-${fret}`;
  }

  getDimensions() {
    return {
      numStrings: this.numStrings,
      numFrets: this.numFrets,
    };
  }

  getAllPositions(): FretPosition[] {
    return Array.from(this.positions.values());
  }

  getPosition(string: number, fret: number): FretPosition | undefined {
    return this.positions.get(this.getPositionKey(string, fret));
  }

  getScalePositions(root: string, type: string): FretPosition[] {
    const scaleNotes = MusicTheoryService.getScaleNotes(root, type);
    return this.getAllPositions().filter((pos) =>
      scaleNotes.some((scaleNote) =>
        MusicTheoryService.areNotesEnharmonic(scaleNote, pos.pitch.note)
      )
    );
  }

  getRandomFretPosition(
    fretRange?: { min: number; max: number },
    activeStrings?: number[]
  ): FretPosition | null {
    // If we have range constraints, filter positions accordingly
    if (fretRange || activeStrings) {
      const allPositions = this.getAllPositions();
      const validPositions = allPositions.filter(
        (position) =>
          // Apply fret range filter if provided
          (!fretRange || (position.fret >= fretRange.min && position.fret <= fretRange.max)) &&
          // Apply active strings filter if provided
          (!activeStrings || activeStrings.includes(position.string))
      );

      // If we have valid positions within the constraints, select one randomly
      if (validPositions.length > 0) {
        const randomIndex = Math.floor(Math.random() * validPositions.length);
        const chosen = validPositions[randomIndex];
        if (!chosen) {
          throw new CoreError(
            'Random selection failed: index out of bounds',
            'INTERNAL_LOGIC_ERROR',
            { randomIndex, total: validPositions.length }
          );
        }
        return chosen;
      }

      // No valid positions found with constraints, return null
      return null;
    }

    // Original implementation for backward compatibility or fallback
    const string = Math.floor(Math.random() * this.numStrings);
    const fret = Math.floor(Math.random() * this.numFrets);
    const position = this.getPosition(string, fret);
    if (!position) {
      throw new CoreError('Invalid position generated randomly.', 'INTERNAL_LOGIC_ERROR', {
        generatedString: string,
        generatedFret: fret,
      });
    }
    return position;
  }

  public getInstrumentDefinition(): InstrumentDefinition {
    return this.instrument;
  }

  public getNumStrings(): number {
    return this.numStrings;
  }
}
